import type { Maybe } from '@adornis/base/utilTypes.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { getSelectableUebertragYears, LAK_UEBERTRAG_FALLBACK_YEAR } from '../lakCreditStart.js';

export interface PreviousLAKHoursDialogResult {
  value: Maybe<number>;
  year: number;
}

const PreviousLAKHoursDialog = stateful(
  (props: {
    value: Maybe<number>;
    year: Maybe<number>;
    resolve: (value: PreviousLAKHoursDialogResult) => void;
    reject: () => void;
  }) => {
    const [hours, setHours] = useState(props.value);
    const [year, setYear] = useState(props.year ?? LAK_UEBERTRAG_FALLBACK_YEAR);

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'Übernommene LAK-Stunden ändern', close: props.reject, closeLabel: 'Dialog schließen' })}
        <p>
          Stunden, die vor der Berechnung im Portal auf dem Lebensarbeitszeitkonto angesammelt wurden. Sie bilden den
          Anfang der Berechnung, alle berechneten Intervalle werden darauf addiert.
        </p>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Übernommene Stunden</span>
          ${NumberInput({
            value: hours,
            onPicked: value => setHours(value),
            placeholder: 'Keine Stunden übernommen',
            placeholderMode: 'hide',
            clearable: true,
            min: 0,
            suffix: 'Std.',
          })}
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Stand zum</span>
          ${DropdownSelectionInput<number>({
            value: year,
            selectables: getSelectableUebertragYears(),
            searchable: false,
            renderString: value => `31.12.${value}`,
            renderItem: value => `31.12.${value}`,
            onPicked: value => setYear(value ?? LAK_UEBERTRAG_FALLBACK_YEAR),
          })}
          <p class="text-sm text-base-content/70">
            Der Übertrag schließt immer ein Kalenderjahr ab. Aus den Verträgen wird ab dem 01.01.${year + 1} gerechnet,
            ${hours ? 'alles davor steckt im Übertrag.' : 'alles davor bleibt unberücksichtigt.'}
          </p>
        </div>

        <button class="btn btn-primary" @click=${() => props.resolve({ value: hours, year })}>Annehmen</button>
      </div>
    `;
  },
);

export async function openPreviousLAKHoursDialog(value: Maybe<number>, year: Maybe<number>) {
  return showDialog<PreviousLAKHoursDialogResult>({
    content: (resolve, reject) => PreviousLAKHoursDialog({ value, year, resolve, reject }),
  });
}
